import { useEffect, useState } from "react";

declare const __APP_VERSION__: string;

interface ParsedVersion {
  parts: number[];
  pre: string[];
}

const MODERN_VERSION_RE = /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;
const LEGACY_RELEASE_VERSION_RE = /^(\d+)\.(\d+)\.(\d+)\.(\d+)\.(\d+)$/;

function parseVersion(version: string): ParsedVersion | null {
  const modern = MODERN_VERSION_RE.exec(version);
  if (modern) {
    return {
      parts: [Number(modern[1]), Number(modern[2]), Number(modern[3])],
      pre: modern[4] ? modern[4].split(".") : [],
    };
  }
  const legacy = LEGACY_RELEASE_VERSION_RE.exec(version);
  if (!legacy) return null;
  return {
    parts: [Number(legacy[1]), Number(legacy[2]), Number(legacy[3])],
    pre: [legacy[4]!, legacy[5]!],
  };
}

function compareVersion(latest: string, current: string): number | null {
  const a = parseVersion(latest);
  const b = parseVersion(current);
  if (!a || !b) return null;
  const length = Math.max(a.parts.length, b.parts.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (a.parts[index] ?? 0) - (b.parts[index] ?? 0);
    if (difference !== 0) return difference;
  }
  if (a.pre.length === 0 && b.pre.length > 0) return 1;
  if (a.pre.length > 0 && b.pre.length === 0) return -1;
  return a.pre.join(".").localeCompare(b.pre.join("."), undefined, { numeric: true });
}

export interface VersionStatus {
  current: string;
  latest: string | null;
  updateAvailable: boolean;
  checking: boolean;
  failed: boolean;
}

export function useVersionStatus(): VersionStatus {
  const current = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "";
  const [latest, setLatest] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const response = await fetch("/api/version", { cache: "no-store", redirect: "error" });
        const body = response.ok ? await response.json() as { version?: string } : null;
        if (!alive) return;
        if (!body?.version || !parseVersion(current) || !parseVersion(body.version)) {
          setFailed(true);
          return;
        }
        setLatest(body.version);
        setFailed(false);
      } catch {
        if (alive) setFailed(true);
      } finally {
        if (alive) setChecking(false);
      }
    };
    void check();
    const interval = window.setInterval(() => void check(), 30_000);
    return () => {
      alive = false;
      window.clearInterval(interval);
    };
  }, [current]);

  const comparison = latest ? compareVersion(latest, current) : null;
  return {
    current,
    latest,
    updateAvailable: comparison !== null && comparison > 0,
    checking,
    failed,
  };
}

function activateUpdate(): void {
  const reload = () => window.location.reload();
  if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) {
    reload();
    return;
  }
  navigator.serviceWorker.ready
    .then(async (registration) => {
      await registration.update();
      registration.waiting?.postMessage({ type: "SKIP_WAITING" });
      window.setTimeout(reload, 1_500);
    })
    .catch(reload);
}

export function SidebarUpdateNotice(): React.JSX.Element | null {
  const status = useVersionStatus();
  if (!status.updateAvailable || !status.latest) return null;
  return (
    <button
      type="button"
      className="ss-sidebar-update"
      onClick={activateUpdate}
      title={`Update ${status.latest}`}
    >
      <strong>Update</strong>
      <span>v{status.latest}</span>
    </button>
  );
}

export function SettingsVersionDetails(): React.JSX.Element {
  const status = useVersionStatus();
  return (
    <dl className="ss-settings-version">
      <div><dt>Current version</dt><dd>{status.current || "Unavailable"}</dd></div>
      <div>
        <dt>Latest server version</dt>
        <dd>{status.checking ? "Checking…" : status.latest ?? (status.failed ? "Unavailable" : "—")}</dd>
      </div>
    </dl>
  );
}
